﻿<template>
  <items-listing-layout class="all-conversations">
    <template #header-left>
      <!-- HEADER TITLE -->
      <listing-header 
        :is-loading="isLoadingInitialData"
        :is-custom-filter-submitting="isCustomFilterSubmitting"
        :entity-type="EEntities.MAILBOXES"
        :default-filters="defaultFilterNavItems"
        :custom-filters="customFilterNavItems"
        :current-filter="currentFilterNavItem"
        :total-item-count="totalItemCount"
        title="All Conversations"
        @save-new-filter="(name) => createCustomFilter(name)"
        @update-custom-filter-name="(name) => updateCustomFilter(name)"
        @delete-custom-filter="(id) => deleteCustomFilter(id)"
        @change-filter="(id) => updateItemTraitSetFromFilterID(id)"
      />
    </template>
    <template v-if="hasEntities" #header-right>
      <div v-if="$screen.isTabletOrDesktop($mq)" class="all-conversations__header-options">
        <new-mailbox-button size="medium" />
      </div>
    </template>
    <template #main-header>
      <!-- NO MAILBOX EMPTY STATE -->
      <el-card v-if="!isLoadingInitialData && !hasEntities" class="all-conversations__no-mailboxes">
        <h5>All Conversations</h5>
        <template v-if="$permissions.isAdminOrOwner">
          <h2>Create your first mailbox</h2>
          <p>Then, you'll be able to manage email conversations here.</p>
          <img :src="mailboxesImg" :srcset="`${mailboxesImg} 1x, ${mailboxesImg2x} 2x, ${mailboxesImg3x} 3x`">
          <new-mailbox-button />
        </template>
        <template v-else>
          <h2>You don't belong to any mailboxes yet</h2>
          <p>Once an account admin invites you to a mailbox, you can view conversations here.</p>
          <img :src="mailboxesImg" :srcset="`${mailboxesImg} 1x, ${mailboxesImg2x} 2x, ${mailboxesImg3x} 3x`">
        </template>
      </el-card>
      <item-filters-panel 
        v-else
        v-model:search-term="itemTraitSet.searchTerm"
        v-model:selected-entity-ids="itemTraitSet.mailboxIDs"
        v-model:selected-assignee-ids="itemTraitSet.assigneeIDs"
        v-model:assigned-not-to-you="itemTraitSet.assignedNotToYou"
        v-model:selected-job-appointee-groupings-string="itemTraitSet.jobAppointeeGroupingsString"
        v-model:selected-customer-ids="itemTraitSet.customerIDs"
        v-model:selected-status-ids="itemTraitSet.statusIDs"
        v-model:status-type="itemTraitSet.statusType"
        v-model:selected-priority-ids="itemTraitSet.priorityIDs"
        v-model:selected-due-type="itemTraitSet.dueType"
        v-model:selected-including-tag-ids="itemTraitSet.includingTagIDs"
        v-model:selected-excluding-tag-ids="itemTraitSet.notIncludingTagIDs"
        v-model:created-within-days="itemTraitSet.createdWithinDays"
        v-model:last-updated-within-days="itemTraitSet.lastUpdatedWithinDays"
        v-model:only-watching="itemTraitSet.onlyWatching"
        v-model:is-spam="itemTraitSet.isSpam"
        v-model:start-due-date="itemTraitSet.startDueDate"
        v-model:end-due-date="itemTraitSet.endDueDate"
        v-model:selected-sort="itemTraitSet.sort"
        :is-disabled="hasSelectedRows"
        :is-loading="isLoadingInitialData"
        :is-default-item-trait-set="isDefaultItemTraitSet"
        :entity-options="entities"
        :assignee-options="assignees"
        :job-options="jobs"
        :priority-options="priorities"
        :active-status-options="activeStatuses"
        :inactive-status-options="inactiveStatuses"
        :tag-options="tags"       
        :due-date-options="dueDateOptions"
        is-all-convos
        @reset-filters="resetItemTraitSet"
      />
    </template>
    <template v-if="hasEntities" #main-content>
      <!-- TABLE -->
      <home-table
        ref="homeTable"
        :class="{'all-conversations__table-no-page-buffer': !hasPages}"
        :type="EEntities.MAILBOXES"
        :table-data="results"
        :custom-fields="customFields"
        :is-loading="isLoadingResults"
        class="results"
        from-all
        @select-rows="updateSelectedRows"
      />
      <!-- PAGINATION -->
      <el-pagination
        v-if="hasPages"
        :page-size="itemsPerPage"
        :pager-count="5"
        :current-page="currentPage"
        :total="totalItemCount"
        background
        layout="prev, pager, next"
        class="all-conversations__pagination"
        @current-change="(page) => changePage(page)"
      />
    </template>
    <template #modals>
      <!-- BULK EDIT (DESKTOP) -->      
      <bulk-edit-panel
        v-if="hasSelectedRows" 
        :selected-ids="selectedItemIDs"
        :selected-entity-ids="entityIDsForSelectedRows"
        :entity-type="EEntities.MAILBOXES"
        :is-spam-filter-selected="itemTraitSet.isSpam"
        @edit-canceled="($refs.homeTable as { clearBulkEdit(): void }).clearBulkEdit()"
        @edit-completed="reloadData()"
        @move-items-clicked="showMoveItemsModal=true"
        @add-comment-clicked="showAddCommentModal=true"
        @merge-convos-clicked="showMergeConvosModal=true"
      />
      <!-- BULK MOVE MAILBOX -->
      <!-- Only will be accessible if all selected items are from a single mailbox since
           move-items-clicked is only possibly emitted in that scenario-->
      <bulk-move-items-modal
        v-if="showMoveItemsModal"
        :entity-id="entityIDsForSelectedRows[0]"
        :selected-item-ids="selectedItemIDs"
        is-convo-related
        @edit-canceled="showMoveItemsModal=false"
        @edit-completed="reloadData();showMoveItemsModal=false;"
      />
      <!-- MERGE CONVOS -->
      <!-- Only will be accessible if all selected items are from a single mailbox since
           merge-items-clicked is only possibly emitted in that scenario-->
      <bulk-merge-conversations-modal
        v-if="showMergeConvosModal"
        :mailbox-id="entityIDsForSelectedRows[0]"
        :selected-convo-ids="selectedItemIDs"
        @edit-canceled="showMergeConvosModal=false"
        @edit-completed="reloadData();showMergeConvosModal=false;"
      />
      <!-- BULK ADD COMMENT -->
      <bulk-add-comment-modal
        v-if="showAddCommentModal"
        :selected-item-ids="selectedItemIDs"
        is-convo-related
        @edit-canceled="showAddCommentModal=false"
        @edit-completed="reloadData();showAddCommentModal=false;"
      />
    </template>
  </items-listing-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import dayjs from '@/helpers/dayjs'

/* Mixins */
import AllLandingMixin from '@/mixins/all-landing.mixin'

/* Components */
import mailboxesImg from '@/assets/img/entities/illustration-mailbox-empty-state.png'
import mailboxesImg2x from '@/assets/img/entities/illustration-mailbox-empty-state@2x.png'
import mailboxesImg3x from '@/assets/img/entities/illustration-mailbox-empty-state@3x.png'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import TextAssistant from '@/helpers/text-assistant'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'

/* Data types */
import { EEntities, SortOptionValues } from '@/types'
import {
  IBreadcrumbItemMenuItem,
  IHomeTableItem,
} from '@/types/data-objects/items'
import { IItemTraitSet } from '@/types/data-objects/listing'
import {
  IConvosOutputAllFilterOptions,
  IConvosOutputSearchConvo,
  ICustomField,
} from '@/api/types/response.types'
import {
  IConvosInputManageCustomFilter,
  IConvosInputUpdateCustomFilter,
} from '@/api/types/request.types'

/* API */
import ConversationsAPI from '@/api/modules/convos.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import CustomFiltersAPI from '@/api/modules/custom-filters.api'

export default defineComponent({
  name: 'AllConversations',

  components: {
    ItemsListingLayout,
  },

  extends: AllLandingMixin,

  data() {
    return {
      isAllConvos: true,
      RouteAssistant,
      mailboxesImg,
      mailboxesImg2x,
      mailboxesImg3x,

      showMergeConvosModal: false,
      customFields: [] as ICustomField[],
    }
  },

  head: {
    title: 'All Conversations',
  },

  beforeUnmount() {
    this.endPolling()
  },

  async created() {
    this.isLoadingInitialData = true
    await Promise.all([this.fetchFilterOptions()])
    LocalStore.lastItemListRouteVisited = RouteAssistant.getAllConversationsRoute(
      this.accountID,
    )
    this.startPolling()
    this.postCreated()
    this.isLoadingInitialData = false
  },

  methods: {
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      // A load that finishes after the user left would otherwise start a poller nothing clears
      if (this.isUnmounted) {
        return
      }
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async reloadData() {
      await Promise.all([this.fetchFilterOptions(), this.fetchResults(false)])
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await MailboxesAPI.get_MostRecentLastUpdatedEpochTime()
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await Promise.all([this.fetchFilterOptions(), this.fetchResults(false)])
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async fetchResults(showResultsLoading: boolean = true) {
      this.selectedItemIDs = []
      this.isLoadingResults = showResultsLoading
      const params = {
        page: this.currentPage,
        mailbox_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.mailboxIDs,
        ),
        priority_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.priorityIDs,
        ),
        status_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.statusIDs,
        ),
        status_type: this.itemTraitSet.statusType,
        assignee_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.assigneeIDs,
        ),
        assigned_not_to_you: this.itemTraitSet.assignedNotToYou,
        job_appointee_groupings_string: this.itemTraitSet
          .jobAppointeeGroupingsString,
        including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.includingTagIDs,
        ),
        not_including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.notIncludingTagIDs,
        ),
        start_due_date: this.itemTraitSet.startDueDate,
        end_due_date: this.itemTraitSet.endDueDate,
        due_type: this.itemTraitSet.dueType,
        sort: this.itemTraitSet.sort,
        search_term: this.itemTraitSet.searchTerm,
        only_watching: this.itemTraitSet.onlyWatching,
        created_within_days: this.itemTraitSet.createdWithinDays,
        last_updated_within_days: this.itemTraitSet.lastUpdatedWithinDays,
        customer_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.customerIDs,
        ),
        is_spam: this.itemTraitSet.isSpam,
      }

      try {
        // If hiding the load, don't reset total item count so there
        // is no "pulse" on the dashboard filter picker
        if (showResultsLoading) {
          this.totalItemCount = null
        }
        // @ts-ignore
        const response = await ConversationsAPI.get_Search(
          params.page,
          params.mailbox_ids,
          params.customer_ids,
          params.sort,
          params.is_spam,
          params.priority_ids,
          params.status_ids,
          params.status_type,
          params.assignee_ids,
          params.assigned_not_to_you,
          params.job_appointee_groupings_string,
          params.including_tag_ids,
          params.not_including_tag_ids,
          params.due_type,
          params.start_due_date,
          params.end_due_date,
          params.search_term,
          params.only_watching,
          params.created_within_days,
          params.last_updated_within_days,
        )

        // Store access epoch time
        this.lastAccessEpochTime = response.accessEpochTime

        // Pagination
        this.totalItemCount = response.totalConversationCount

        // Custom fields flagged to show on listings, across the mailboxes in these results
        this.customFields = response.listCustomFields || []

        // Results
        // @ts-ignore
        this.results = response.listConversations.map(item =>
          this.mapResultsToTableFormat(item),
        )

        const breadCrumbItems = [] as IBreadcrumbItemMenuItem[]

        response.listConversations.forEach(t => {
          breadCrumbItems.push({
            itemID: t.id,
            entityID: t.mailbox.id,
            entityName: t.mailbox.name,
            entityType: EEntities.MAILBOXES,
            title: t.title,
            refNumber: t.refNumber,
          } as IBreadcrumbItemMenuItem)
        })

        BreadcrumbAssistant.cacheBreadcrumbItemMenuItems(
          breadCrumbItems,
        )
      } catch (error) {
        LocalStore.clearAllConvosSavedQuery(this.accountID)
        this.$confirm(
          `Let's reset your mailbox filters so you can try again.`,
          {
            dangerouslyUseHTMLString: true,
            center: true,
            type: 'warning',
            title: 'This filter is no longer available',
          },
        ).then(() => {
          this.itemTraitSet.mailboxIDs = []
        })
      }
      this.isLoadingResults = false
    },

    async fetchFilterOptions() {
      try {
        const response = await ConversationsAPI.get_All_FilterOptions()
        this.hydrateFilterOptions(response)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    hydrateFilterOptions(data: IConvosOutputAllFilterOptions) {
      this.hydrateFilterOptionsForListing_Base(data)
      this.entities = data.listMailboxes

      this.customFilterNavItems = data.listCustomFilters.map(c => ({
        value: ItemFilterAssistant.getCustomFilterIdentifier(c.id),
        label: c.name,
        icon: 'filter',
      }))
      this.customFilterItemTraitSets = data.listCustomFilters.map(c => ({
        id: c.id,
        itemTraitSet: {
          priorityIDs: c.listPriorityIDs ? c.listPriorityIDs : [],
          statusIDs: c.listStatusIDs ? c.listStatusIDs : [],
          statusType: c.statusType ? c.statusType : '',
          assigneeIDs: c.listAssigneeIDs ? c.listAssigneeIDs : [],
          assignedNotToYou: c.assignedNotToYou,
          jobAppointeeGroupingsString: ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
            c.listJobAppointeeGroupings,
          ),
          dueType: c.dueType ? c.dueType : null,
          includingTagIDs: c.listIncludingTagIDs ? c.listIncludingTagIDs : [],
          notIncludingTagIDs: c.listNotIncludingTagIDs
            ? c.listNotIncludingTagIDs
            : [],
          startDueDate: c.startDueDate
            ? dayjs(c.startDueDate).format('YYYY-MM-DD')
            : null,
          endDueDate: c.endDueDate
            ? dayjs(c.endDueDate).format('YYYY-MM-DD')
            : null,
          searchTerm: c.searchTerm ? c.searchTerm : '',
          onlyWatching: c.onlyWatching,
          createdWithinDays: c.createdWithinDays,
          lastUpdatedWithinDays: c.lastUpdatedWithinDays,
          creatorIDs: [], // Never used, default it so the comparison is equal in isItemTraitSetEquivalent()
          customerIDs: c.listCustomerIDs ? c.listCustomerIDs : [],
          isSpam: c.isSpam,
          sort: SortOptionValues.UPDATED_RECENT,
          projectIDs: [], // Unused, default it so the comparison is equal in isItemTraitSetEquivalent()
          customFieldFiltersString: '', // Unused, default it so the comparison is equal in isItemTraitSetEquivalent()
          mailboxIDs: c.listMailboxIDs ? c.listMailboxIDs : [],
        } as IItemTraitSet,
      }))
    },

    async createCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          listMailboxIDs: this.itemTraitSet.mailboxIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as IConvosInputManageCustomFilter
        const response = await ConversationsAPI.post_CreateCustomFilter(
          formData,
        )
        await this.fetchFilterOptions()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async updateCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          listMailboxIDs: this.itemTraitSet.mailboxIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as IConvosInputUpdateCustomFilter
        const response = await ConversationsAPI.put_UpdateCustomFilter(
          ItemFilterAssistant.getIDOfFilterIdentifier(
            this.currentFilterNavItem.value,
          ),
          formData,
        )
        await this.fetchFilterOptions()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    handleDeleteCustomFilter(identifier: string) {
      this.$confirm(
        `<strong>This cannot be undone.</strong>
        <br>Are you sure you want to delete this?`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: 'Delete this custom filter?',
        },
      ).then(() => {
        this.deleteCustomFilter(identifier)
      })
    },

    async deleteCustomFilter(identifier: string) {
      try {
        const response = await CustomFiltersAPI.delete_Delete(
          ItemFilterAssistant.getIDOfFilterIdentifier(identifier),
        )

        this.fetchFilterOptions()

        if (this.currentFilterNavItem.value !== identifier) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    mapResultsToTableFormat(item: IConvosOutputSearchConvo): IHomeTableItem {
      return {
        ...item,
        parent: item.mailbox,
        entityType: EEntities.MAILBOXES,
      }
    },
  },
})
</script>

<style lang="scss">
.all-conversations {
  &__table-no-page-buffer {
    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  &__header-options {
    display: flex;
    @include flex-row-horizontal-spacing(8px);
  }

  &__no-mailboxes {
    margin-top: 20px;
    padding: 20px 0;
    .el-card__body {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: auto;
      > img {
        padding-bottom: 20px;
      }
    }
  }

  &__pagination {
    margin-top: 24px;
    text-align: center;

    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  .container.main-container {
    /* For home, "mobile" switch happens at medium */
    @include media-breakpoint-down(md) {
      max-width: 100%;
    }
  }
}
</style>
